import { AccountInfo } from '@azure/msal-browser';

import {
  ensureMsalInitialized,
  msalInstance,
} from '@frontend/features/auth/config/msalInstance';
import { getAuthMode } from '@frontend/features/auth/util/util.auth.getAuthMode';
import { getLocalToken } from '@frontend/features/auth/util/util.auth.localToken';
import { parseJwt } from '@frontend/features/auth/util/util.auth.parseJwt';

/**
 * Gets the active user account based on current authentication mode
 *
 * **E2E Mode** (token in localStorage):
 * - Creates mock AccountInfo from localStorage token
 * - Works on all environments (localhost, dev, qa, prod)
 *
 * **MSAL Mode** (no token in localStorage):
 * - Returns active account from MSAL library
 * - Requires user to be logged in via Azure AD
 *
 * @returns AccountInfo object or null if no active account
 */
export async function getActiveAccount(): Promise<AccountInfo | null> {
  const authMode = getAuthMode();

  // E2E mode: create mock account from localStorage token
  if (authMode === 'e2e') {
    const token = getLocalToken();
    if (!token) return null;

    try {
      const claims = parseJwt(token);

      // Create mock AccountInfo structure that matches MSAL
      return {
        homeAccountId: claims.oid || '',
        environment: 'e2e',
        tenantId: 'e2e',
        username: claims.email || claims.upn || '',
        localAccountId: claims.oid || '',
        name: claims.name || '',
        idTokenClaims: claims,
      } as AccountInfo;
    } catch (error) {
      console.error('[AUTH] Failed to parse E2E token:', error);
      return null;
    }
  }

  // MSAL mode: use Azure AD authentication
  await ensureMsalInitialized();

  const activeAccount = msalInstance.getActiveAccount();

  if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
    // If no active account is set but accounts are available, set the first one as active
    const accounts = msalInstance.getAllAccounts();
    if (accounts[0]) {
      msalInstance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
  }

  return activeAccount;
}
